import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { WebsiteService } from './website.service';

type AnalyticsWindow = Window & { dataLayer?: unknown[]; fbq?: (...args: unknown[]) => void };

const PIXEL_RETRY_MS = 500;
const PIXEL_RETRIES = 20;

// gtag.js only understands the Arguments object that its own gtag() pushes, not an array
function asArguments(..._values: unknown[]): IArguments {
  // eslint-disable-next-line prefer-rest-params
  return arguments;
}

/**
 * The shop's Google tags. Everything here does nothing on the server, where there is no window,
 * and for a shop with no id of its own.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {

  private document = inject(DOCUMENT);
  private website = inject(WebsiteService).website;
  private loaded = false;

  load(): void {
    const window = this.window();
    const website = this.website();
    if (!window || !website || this.loaded) {
      return;
    }
    this.loaded = true;
    const { gtagId, gtmId } = website;
    if (gtagId) {
      this.script(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gtagId)}`);
      this.push('js', new Date());
      this.push('config', gtagId);
    }
    if (gtmId) {
      (window.dataLayer ??= []).push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      this.script(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`);
    }
  }

  /** A GA4 event. Queued if gtag.js has not arrived yet, which it reads in order once it does. */
  event(name: string, parameters: { currency?: string | null; [key: string]: unknown }): void {
    const gtagId = this.website()?.gtagId;
    const currency = parameters.currency || 'UAH';
    if (gtagId) {
      this.push('event', name, { ...parameters, currency, send_to: gtagId });
    }
    if (name === 'purchase') {
      // The order code is the event id, so Meta drops a repeat of the same purchase, a reload included.
      const id = parameters['transaction_id'];
      this.pixel(['track', 'Purchase', { value: parameters['value'], currency }, ...(id ? [{ eventID: id }] : [])]);
    }
  }

  /** The pixel itself comes from the tag manager, which an order page can render before. */
  private pixel(args: unknown[], retries = PIXEL_RETRIES): void {
    const window = this.window();
    if (!window) {
      return;
    }
    if (window.fbq) {
      window.fbq(...args);
    } else if (retries) {
      setTimeout(() => this.pixel(args, retries - 1), PIXEL_RETRY_MS);
    }
  }

  private window(): AnalyticsWindow | null {
    return this.document.defaultView;
  }

  private push(...args: unknown[]): void {
    const window = this.window();
    if (!window) {
      return;
    }
    (window.dataLayer ??= []).push(asArguments(...args));
  }

  private script(source: string): void {
    const script = this.document.createElement('script');
    script.async = true;
    script.src = source;
    this.document.head.appendChild(script);
  }

}
